import { Fragment, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LOCALE_DE, VERMITTLER_UMSATZ_ERFASST_AB_JAHR } from '@rechnungen/shared';
import {
  AlertTriangle,
  ArrowRight,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  Euro,
  FileSpreadsheet,
  TrendingUp,
  Trophy,
  Users,
  Users2,
  XCircle,
} from 'lucide-react';
import { api } from '@/lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BlockLayoutMenu } from '@/components/block-layout-menu';
import { cn, formatCurrency, formatDate } from '@/lib/utils';
import { SectionTitle } from '@/components/brand';
import { VermittlerUmsatzHinweis } from '@/components/vermittler-umsatz-hinweis';
import { useAuth } from '@/lib/auth-context';
import { usePermissions } from '@/lib/use-permissions';
import { useBlockLayout } from '@/lib/use-block-layout';
import { DASHBOARD_BLOCKS } from './dashboard-blocks';
import { pickKanzleiTileState } from './dashboard-kanzlei-tile-state';

interface Kpis {
  offeneRechnungen: { count: number; summe: number };
  umsatzYtd: number;
  umsatzHochrechnung: number | null;
  monatsumsatz: { year: number; month: number; netto: number };
  kundenAktiv: number;
  projekteAktiv: number;
  ueberfaellig: { count: number; summe: number };
}

interface TimelineEntry {
  year: number;
  month: number;
  label: string;
  yearMonth: string;
  netto: number;
}

interface TopCustomer {
  kundeNr: string | null;
  kunde: string;
  netto: number;
  anzahl: number;
}

interface DsoKunde {
  kundeNr: string | null;
  kunde: string;
  avgDays: number;
  avgVerzug: number;
  count: number;
}

interface DsoData {
  avgZahlungsdauer: number | null;
  avgVerzug: number | null;
  pktlichQuote: number | null;
  count: number;
  byKunde: DsoKunde[];
}

interface PersonalcontrollingSummary {
  year: number;
  month: number;
  consultantsAktiv: number;
  cellsErfasst: number;
  avgAuslastung: number | null;
  warnungenCount: number;
}

interface AusgabenSummary {
  jahr: number;
  monat: number;
  standDatum: string;
  quelle: string;
  /**
   * MONAT = Monats-Stand, JAHR = Jahres-Master (letzte Kanzlei-Auswertung des
   * Jahres). Entscheidet die Beschriftung: bei JAHR ist `gesamtAufwand` ein
   * JAHRES-Wert und darf nicht als Monatswert überschrieben werden.
   */
  periodenTyp: 'MONAT' | 'JAHR';
  gesamtAufwand: number;
}

interface LohnSummary {
  jahr: number;
  monat: number;
  standDatum: string;
  quelle: string;
  gesamtBrutto: number;
  gesamtArbeitgeberkosten: number;
  mitarbeiterAnzahl: number;
}

type PeriodStatus = 'billed' | 'running' | 'upcoming' | 'due' | 'overdue' | 'na';

interface DuePeriod {
  von: string;
  bis: string;
  label: string;
  status: PeriodStatus;
  reNr: string | null;
}

interface DueRow {
  projektId: number;
  projektName: string;
  kundeNr?: string;
  kundeName: string;
  rechnungszyklus: string;
  schaetzungNetto: number;
  perioden: DuePeriod[];
}

export function DashboardPage() {
  const now = new Date();
  const { user } = useAuth();
  const { canWrite } = usePermissions();
  // Vorname aus vollem Namen ziehen (für den Gruß im Hero)
  const firstName = (user?.name ?? '').split(/\s+/)[0] ?? 'Benutzer';
  const [selectedYear, setSelectedYear] = useState(now.getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getMonth() + 1);
  const layout = useBlockLayout('dashboard', DASHBOARD_BLOCKS);

  const kpisQuery = useQuery({
    queryKey: ['dashboard', 'kpis', selectedYear, selectedMonth],
    queryFn: async () =>
      (await api.get<Kpis>('/dashboard/kpis', { year: selectedYear, month: selectedMonth })).data,
  });

  // Fix 17.08.2026: folgte vorher immer `now`, unabhaengig von der Monats-
  // navigation links (selectedYear/selectedMonth) — "Top 5 Kunden {year}"
  // im Titel aenderte sich beim Zurueckblaettern also nie, obwohl der Titel
  // selbst einen Jahresbezug suggeriert. Anders als personalcontrollingQuery/
  // ausgabenQuery, die BEWUSST jahresunabhaengig sind (siehe deren Kommentare),
  // gab es hier keine Begruendung dafuer — nur ein Uebersehen.
  const timelineQuery = useQuery({
    queryKey: ['dashboard', 'timeline', selectedYear, selectedMonth],
    queryFn: async () =>
      (
        await api.get<TimelineEntry[]>('/dashboard/revenue-timeline', {
          endYear: selectedYear,
          endMonth: selectedMonth,
          length: 12,
        })
      ).data,
  });

  const topCustomersQuery = useQuery({
    queryKey: ['dashboard', 'top-customers', selectedYear],
    queryFn: async () =>
      (
        await api.get<TopCustomer[]>('/dashboard/top-customers', {
          year: selectedYear,
          limit: 5,
        })
      ).data,
  });

  const dueQuery = useQuery({
    queryKey: ['billing-periods', 'due'],
    queryFn: async () => (await api.get<DueRow[]>('/billing-periods/due')).data,
  });

  const dsoQuery = useQuery({
    queryKey: ['dashboard', 'dso'],
    queryFn: async () => (await api.get<DsoData>('/dashboard/dso')).data,
    // DSO ändert sich selten — 5 min Cache reicht
    staleTime: 5 * 60 * 1000,
  });

  // Personalcontrolling-Kachel (Review 10.08.2026) — immer aktueller Monat,
  // unabhängig von der Monatsumsatz-Navigation links daneben.
  // Fix 19.08.2026: Backend gated diesen Endpoint jetzt auf ADMIN+Buchhalter
  // (routes/dashboard.ts) — ohne `enabled: canWrite` liefe die Query für
  // VIEWER endlos ins 403, gleiches Muster wie ausgabenQuery/lohnQuery unten.
  const personalcontrollingQuery = useQuery({
    queryKey: ['dashboard', 'personalcontrolling-summary', now.getFullYear(), now.getMonth() + 1],
    queryFn: async () =>
      (
        await api.get<PersonalcontrollingSummary>('/dashboard/personalcontrolling-summary', {
          year: now.getFullYear(),
          month: now.getMonth() + 1,
        })
      ).data,
    enabled: canWrite,
    staleTime: 5 * 60 * 1000,
  });

  // Ausgaben-Kachel: neuester importierter SuSa-Monat, unabhaengig vom
  // Kalendermonat — die Steuerkanzlei liefert ihre PDFs typischerweise erst
  // Wochen nach Monatsende (siehe Plan "Ausgaben-Reporting für fakt").
  // Fix 17.08.2026: Backend gated diesen Endpoint auf ADMIN+Buchhalter
  // (routes/dashboard.ts) — ohne `enabled: canWrite` liefe die Query für
  // VIEWER endlos ins 403, `data` bliebe fuer immer `undefined`, und die
  // Kachel zeigte faelschlich "Noch kein Monat importiert" statt "kein
  // Zugriff". Gleiches Muster wie ausgaben-page.tsx (dort bereits korrekt).
  const ausgabenQuery = useQuery({
    queryKey: ['dashboard', 'ausgaben-summary'],
    queryFn: async () =>
      (await api.get<AusgabenSummary | null>('/dashboard/ausgaben-summary')).data,
    enabled: canWrite,
    staleTime: 5 * 60 * 1000,
  });

  // Lohn-Kachel: analog ausgabenQuery, gleiche Begründung (Backend gated auf
  // ADMIN+Buchhalter, siehe routes/dashboard.ts).
  const lohnQuery = useQuery({
    queryKey: ['dashboard', 'lohn-summary'],
    queryFn: async () => (await api.get<LohnSummary | null>('/dashboard/lohn-summary')).data,
    enabled: canWrite,
    staleTime: 5 * 60 * 1000,
  });

  const shiftMonth = (delta: number) => {
    let y = selectedYear;
    let m = selectedMonth + delta;
    while (m < 1) {
      m += 12;
      y -= 1;
    }
    while (m > 12) {
      m -= 12;
      y += 1;
    }
    setSelectedYear(y);
    setSelectedMonth(m);
  };
  const isCurrentMonth = selectedYear === now.getFullYear() && selectedMonth === now.getMonth() + 1;
  const monthLabel = new Date(selectedYear, selectedMonth - 1, 1).toLocaleDateString(LOCALE_DE, {
    month: 'long',
    year: 'numeric',
  });

  // Bericht-Export (05.05.2026): User klickt → Browser laedt PDF mit
  // Cookies/CSRF, Backend generiert via jsPDF on-the-fly. Nicht via api.get
  // weil das ein Buffer waere und kein Download trigger.
  const exportReport = (): void => {
    const params = new URLSearchParams({
      year: String(selectedYear),
      month: String(selectedMonth),
    });
    const url = `/api/dashboard/report?${params.toString()}`;
    const a = document.createElement('a');
    a.href = url;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="space-y-6">
      {/* BL-058 (18.05.2026): Setup-Checklist solange Pflicht-Konfiguration
          fehlt. Verhindert „erste Rechnung ohne USt-ID" oder ohne IBAN. */}
      <SetupChecklistBanner />

      {/* ================ HERO ================================ */}
      {kpisQuery.data && (
        <HeroCard
          userName={firstName}
          offeneCount={kpisQuery.data.offeneRechnungen.count}
          offeneSumme={kpisQuery.data.offeneRechnungen.summe}
          ueberfaelligCount={kpisQuery.data.ueberfaellig.count}
          umsatzYtd={kpisQuery.data.umsatzYtd}
          umsatzJahr={selectedYear}
          kundenAktiv={kpisQuery.data.kundenAktiv}
          projekteAktiv={kpisQuery.data.projekteAktiv}
          onExportReport={exportReport}
        />
      )}

      {kpisQuery.error && (
        <p className="text-sm text-danger">Fehler: {(kpisQuery.error as Error).message}</p>
      )}

      <div className="flex justify-end">
        <BlockLayoutMenu blocks={DASHBOARD_BLOCKS} layout={layout} />
      </div>

      {layout.visibleIds.map((id) => (
        <Fragment key={id}>
          {id === 'kpis' && kpisQuery.data && (
            // BL-064 (20.05.2026): 3. KPI-Card für DSO-Metrik hinzugefügt.
            // Review 10.08.2026: 4. Karte für Personalcontrolling — Grid auf
            // 2×2 (md) / 4×1 (xl) umgestellt, damit die Reihe nicht umbricht.
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
              {/* Monatsumsatz mit Navigation */}
              <Card>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium text-muted-foreground">
                    Umsatz {monthLabel}
                  </CardTitle>
                  <Calendar className="h-4 w-4 text-primary" />
                </CardHeader>
                <CardContent>
                  <div className="flex items-baseline gap-2">
                    <div className="text-2xl font-bold">
                      {formatCurrency(kpisQuery.data.monatsumsatz.netto)}
                    </div>
                    <AmountKind kind="netto" />
                  </div>
                  {kpisQuery.data.monatsumsatz.netto === 0 && (
                    <div className="mt-1 text-[11px] text-ink-muted">
                      Noch keine Rechnungen in diesem Monat
                    </div>
                  )}
                  <div className="mt-1 flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => shiftMonth(-1)}
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                    {!isCurrentMonth && (
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 text-xs"
                        onClick={() => {
                          setSelectedYear(now.getFullYear());
                          setSelectedMonth(now.getMonth() + 1);
                        }}
                      >
                        Aktuell
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-6 w-6"
                      onClick={() => shiftMonth(1)}
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Fix 12.08.2026: das Label war auf `now.getFullYear()` festverdrahtet,
                    der Wert folgt aber `selectedYear` aus der Monatsnavigation links
                    (Query-Key Z. 106). Ein Klick zurück in den Dezember 2025 zeigte
                    damit „Umsatz 2026" über einer 2025er-Zahl. Und „(Plan-Hochrechnung)"
                    stand im Label, obwohl die Hochrechnung für vergangene Jahre
                    bewusst `null` ist (dashboard-queries.ts) — der Zusatz erscheint
                    jetzt nur, wenn es wirklich eine gibt. */}
              <KpiCard
                icon={Euro}
                label={
                  kpisQuery.data.umsatzHochrechnung
                    ? `Umsatz ${selectedYear} (Plan-Hochrechnung)`
                    : `Umsatz ${selectedYear}`
                }
                value={formatCurrency(
                  kpisQuery.data.umsatzHochrechnung ?? kpisQuery.data.umsatzYtd,
                )}
                kind="netto"
                subvalue={
                  kpisQuery.data.umsatzHochrechnung
                    ? `YTD: ${formatCurrency(kpisQuery.data.umsatzYtd)} netto`
                    : // BL-352 (16.09.2026): fehlende Hochrechnung bewusst begründen
                      // statt kommentarlos nur die reine YTD-Zahl zu zeigen — Januar
                      // UND Februar (seit dem Folgemonat-Sicherheitsabstand) zeigen
                      // sonst einen unerklärten Unterschied zu jedem anderen Monat.
                      // Nur für das LAUFENDE Jahr relevant: bei einem vergangenen Jahr
                      // ist `null` schlicht "Jahr abgeschlossen, keine Hochrechnung
                      // nötig" und braucht keine Erklärung.
                      selectedYear === now.getFullYear()
                      ? 'Hochrechnung ab März verfügbar — noch kein Fakturierungsmonat abgeschlossen'
                      : undefined
                }
                hint={<VermittlerUmsatzHinweis jahre={[selectedYear]} />}
              />

              {/* DSO — Zahlungsdauer-Kennzahl (BL-064) */}
              <DsoKpiCard dso={dsoQuery.data ?? null} />

              {/* Personalcontrolling — Auslastung aktueller Monat (Review 10.08.2026) */}
              <PersonalcontrollingKpiCard
                summary={personalcontrollingQuery.data ?? null}
                canWrite={canWrite}
              />
            </div>
          )}

          {id === 'kanzlei' && (
            // Kanzlei-Daten (FiBu+LoBu-Reporting) — bewusst mit eigenem Trenner
            // statt nahtlos ans 4er-Umsatz-Grid angeschlossen: die Zahlen
            // stammen aus einer ANDEREN Quelle (SuSa-/Lohnjournal-Import der
            // Steuerkanzlei, nicht fakts eigenes Orderbuch) und sollen dadurch
            // auch visuell nie mit den Fakt-/Umsatzzahlen verwechselt werden
            // (User-Wunsch 13.08.2026). "Kanzlei-Daten" statt "FiBu-Daten"
            // (Review 19.08.2026), weil dieser Trenner auch die LoBu-Kachel
            // umfasst — FiBu und LoBu sind gleichrangige, getrennte
            // Buchhaltungs-Disziplinen.
            <>
              <div className="flex items-center gap-3 pt-2">
                <div className="h-px flex-1 bg-hair" />
                <span className="shrink-0 text-[10px] font-medium uppercase tracking-widest text-ink-muted">
                  Kanzlei-Daten · separate Quelle (Steuerkanzlei)
                </span>
                <div className="h-px flex-1 bg-hair" />
              </div>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                <AusgabenKpiCard
                  summary={ausgabenQuery.data ?? null}
                  canWrite={canWrite}
                  isLoading={ausgabenQuery.isLoading}
                  isError={ausgabenQuery.isError}
                />
                <LohnKpiCard
                  summary={lohnQuery.data ?? null}
                  canWrite={canWrite}
                  isLoading={lohnQuery.isLoading}
                  isError={lohnQuery.isError}
                />
              </div>
            </>
          )}

          {id === 'verlauf-topkunden' && (
            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
              <Card className="lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <TrendingUp className="h-4 w-4 text-primary" />
                    Umsatz-Verlauf (letzte 12 Monate)
                  </CardTitle>
                  <CardDescription>Netto nach Leistungsperiode</CardDescription>
                </CardHeader>
                <CardContent>
                  {/* Fix 17.08.2026: `.error` wurde nie geprueft — ein fehlgeschlagener
                      Fetch (Netzwerk/5xx) zeigte dauerhaft "Wird geladen …" statt
                      eines Fehlers, anders als kpisQuery/dueQuery daneben. */}
                  {timelineQuery.error ? (
                    <p className="text-sm text-destructive">
                      Fehler: {(timelineQuery.error as Error).message}
                    </p>
                  ) : timelineQuery.data ? (
                    <RevenueChart data={timelineQuery.data} />
                  ) : (
                    <p className="text-sm text-muted-foreground">Wird geladen …</p>
                  )}
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-base">
                    <Trophy className="h-4 w-4 text-amber-500" />
                    Top 5 Kunden {selectedYear}
                  </CardTitle>
                  <CardDescription>Nach Netto-Umsatz</CardDescription>
                  <VermittlerUmsatzHinweis jahre={[selectedYear]} />
                </CardHeader>
                <CardContent>
                  {topCustomersQuery.error ? (
                    <p className="text-sm text-destructive">
                      Fehler: {(topCustomersQuery.error as Error).message}
                    </p>
                  ) : topCustomersQuery.data ? (
                    <TopCustomersList customers={topCustomersQuery.data} />
                  ) : (
                    <p className="text-sm text-muted-foreground">Wird geladen …</p>
                  )}
                </CardContent>
              </Card>
            </div>
          )}

          {/* BL-064: Zahlungsverhalten pro Kunde (letzten 12 Monate) */}
          {id === 'dso-by-kunde' && dsoQuery.data && dsoQuery.data.byKunde.length > 0 && (
            <DsoByKundeCard dso={dsoQuery.data} />
          )}

          {id === 'due-projects' && <DueProjectsWidget query={dueQuery} />}
        </Fragment>
      ))}
    </div>
  );
}

/**
 * Brutto-/Netto-Label fuer Geldbetraege.
 *
 * Fachliche Konvention im Dashboard:
 * - "Offene Forderungen" / "Ueberfaellig" = Brutto (das, was der Kunde
 *   tatsaechlich zahlen muss, inkl. MwSt — Quelle: orderbuch.betrag)
 * - "Umsatz" / "Hochrechnung" / "Top-Kunden" / "Schaetzung" = Netto
 *   (ohne durchlaufende MwSt — relevant fuer BWA/G&V)
 *
 * Visuell bewusst neutral (kein Signal-Color), damit es nicht mit den
 * semantischen Status-Farben (gruen/gelb/rot) verwechselt wird.
 */
function AmountKind({ kind, className }: { kind: 'brutto' | 'netto'; className?: string }) {
  const title =
    kind === 'brutto'
      ? 'Brutto — inkl. Mehrwertsteuer (was der Kunde zahlt)'
      : 'Netto — ohne Mehrwertsteuer (relevant fuer Umsatz/BWA)';
  return (
    <span
      className={cn(
        'inline-flex items-center text-[10px] font-medium uppercase tracking-wider text-ink-muted',
        className,
      )}
      title={title}
    >
      {kind === 'brutto' ? 'Brutto' : 'Netto'}
    </span>
  );
}

interface KpiCardProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  subvalue?: string;
  /** BL-375: zusätzliche Zeile unter subvalue, z.B. VermittlerUmsatzHinweis. */
  hint?: React.ReactNode;
  variant?: 'default' | 'warning';
  /** Brutto/Netto-Kennzeichnung (Pflicht wenn der value ein Geldbetrag ist) */
  kind?: 'brutto' | 'netto';
}

function KpiCard({
  icon: Icon,
  label,
  value,
  subvalue,
  hint,
  variant = 'default',
  kind,
}: KpiCardProps) {
  const color = variant === 'warning' ? 'text-amber-600' : 'text-primary';
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">{label}</CardTitle>
        <Icon className={`h-4 w-4 ${color}`} />
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline gap-2">
          <div className="text-2xl font-bold">{value}</div>
          {kind && <AmountKind kind={kind} />}
        </div>
        {subvalue && <CardDescription className="mt-1">{subvalue}</CardDescription>}
        {hint && <div className="mt-1">{hint}</div>}
      </CardContent>
    </Card>
  );
}

// =============================================================================
// Revenue Chart (pure-SVG, kein Recharts-Dep nötig)
// =============================================================================

function RevenueChart({ data }: { data: TimelineEntry[] }) {
  const [hoverIdx, setHoverIdx] = useState<number | null>(null);

  const avg = useMemo(() => {
    const nonZero = data.filter((d) => d.netto > 0);
    if (nonZero.length === 0) return 0;
    return nonZero.reduce((s, d) => s + d.netto, 0) / nonZero.length;
  }, [data]);

  const max = Math.max(1, ...data.map((d) => d.netto));
  const chartHeight = 180;
  const barPad = 4;

  return (
    <div className="relative">
      <div className="flex h-[200px] items-end gap-1 px-1">
        {data.map((entry, i) => {
          const height = entry.netto > 0 ? (entry.netto / max) * chartHeight : 2;
          const isHovered = hoverIdx === i;
          const vsAvg = avg > 0 ? ((entry.netto - avg) / avg) * 100 : 0;
          // BL-375: die Zeitleiste kann ueber die BL-371-Erfassungsgrenze
          // hinweg gerendert werden (bis zu 36 Monate) -- an genau dem Monat,
          // an dem sie ueberschritten wird, statt an jedem Balken vorher.
          const istErfassungsgrenze =
            entry.year === VERMITTLER_UMSATZ_ERFASST_AB_JAHR && entry.month === 1;
          return (
            <div
              key={entry.yearMonth}
              className={cn(
                'group relative flex flex-1 flex-col items-center gap-1',
                istErfassungsgrenze && 'border-l border-dashed border-ink-light',
              )}
              style={{ paddingLeft: barPad, paddingRight: barPad }}
              onMouseEnter={() => setHoverIdx(i)}
              onMouseLeave={() => setHoverIdx(null)}
            >
              {istErfassungsgrenze && !isHovered && (
                <div
                  className="absolute bottom-full mb-1 w-max max-w-[9rem] text-center text-[9px] leading-tight text-ink-muted"
                  title="BL-371"
                >
                  Ab hier: inkl. Vermittler-Umsatz
                </div>
              )}
              {isHovered && (
                <div className="absolute bottom-full z-10 mb-2 whitespace-nowrap rounded border bg-panel px-2 py-1 text-xs shadow">
                  <div className="font-medium">{entry.label}</div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="font-mono tabular-nums">{formatCurrency(entry.netto)}</span>
                    <AmountKind kind="netto" />
                  </div>
                  {avg > 0 && entry.netto > 0 && (
                    <div
                      className={cn(
                        'text-[10px]',
                        vsAvg >= 0 ? 'text-emerald-600' : 'text-red-600',
                      )}
                    >
                      {vsAvg >= 0 ? '+' : ''}
                      {vsAvg.toFixed(0)}% vs. Ø
                    </div>
                  )}
                </div>
              )}
              <div
                className={cn(
                  'w-full rounded-t transition-colors',
                  isHovered ? 'bg-primary' : 'bg-primary/70',
                )}
                style={{ height }}
              />
              <div className="text-[10px] text-muted-foreground">{entry.label}</div>
            </div>
          );
        })}
      </div>
      {avg > 0 && (
        <div className="mt-1 flex items-center gap-2 text-xs text-muted-foreground">
          <span className="inline-block h-0.5 w-6 border-t border-dashed border-ink-light" />Ø{' '}
          {formatCurrency(avg)}
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Top Customers List
// =============================================================================

function TopCustomersList({ customers }: { customers: TopCustomer[] }) {
  if (customers.length === 0) {
    return <p className="text-sm text-muted-foreground">Keine Umsätze im laufenden Jahr.</p>;
  }
  const max = customers[0]!.netto;
  return (
    <div className="space-y-3">
      {customers.map((c, i) => (
        <div key={`${c.kundeNr}-${i}`} className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2 min-w-0">
              <span className="font-mono text-xs text-muted-foreground">#{i + 1}</span>
              {c.kundeNr ? (
                <Link
                  to={`/kunden/${c.kundeNr}`}
                  className="truncate font-medium text-primary hover:underline"
                >
                  {c.kunde}
                </Link>
              ) : (
                <span className="truncate font-medium">{c.kunde}</span>
              )}
            </div>
            <span className="shrink-0 font-mono tabular-nums">{formatCurrency(c.netto)}</span>
          </div>
          <div className="h-1.5 w-full overflow-hidden rounded-full bg-bg-deep">
            <div className="h-full bg-primary/70" style={{ width: `${(c.netto / max) * 100}%` }} />
          </div>
        </div>
      ))}
    </div>
  );
}

// =============================================================================
// Fällige-Projekte-Widget (bestehend)
// =============================================================================

function DueProjectsWidget({ query }: { query: ReturnType<typeof useQuery<DueRow[]>> }) {
  const { data, isLoading, error } = query;
  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <Calendar className="h-4 w-4 text-amber-600" />
            Fällige Abrechnungen
          </CardTitle>
          <CardDescription>
            {data?.length
              ? `${flattenCount(data)} offene Abrechnungen in ${data.length} Projekten`
              : 'Keine fälligen Abrechnungen'}
          </CardDescription>
        </div>
        <Button variant="ghost" size="sm" asChild>
          <Link to="/abrechnungszeitraeume">
            Alle
            <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {isLoading && <p className="text-sm text-muted-foreground">Wird geladen …</p>}
        {error && <p className="text-sm text-destructive">Fehler: {(error as Error).message}</p>}
        {data && data.length === 0 && (
          <p className="text-sm text-muted-foreground">
            ✓ Nichts überfällig — alle Projekte auf Stand.
          </p>
        )}
        {data && data.length > 0 && <DueList rows={data} />}
      </CardContent>
    </Card>
  );
}

function flattenCount(rows: DueRow[]): number {
  return rows.reduce((sum, r) => sum + r.perioden.length, 0);
}

function DueList({ rows }: { rows: DueRow[] }) {
  const flat = rows
    .flatMap((r) =>
      r.perioden.map((p) => ({
        projektId: r.projektId,
        projektName: r.projektName,
        kundeNr: r.kundeNr,
        kundeName: r.kundeName,
        schaetzungNetto: r.schaetzungNetto,
        periode: p,
      })),
    )
    .sort((a, b) => {
      if (a.periode.status !== b.periode.status) {
        return a.periode.status === 'overdue' ? -1 : 1;
      }
      return a.periode.von.localeCompare(b.periode.von);
    });

  const top = flat.slice(0, 8);
  const rest = flat.length - top.length;

  return (
    <div className="space-y-2">
      {top.map((item, i) => {
        const params = new URLSearchParams({
          projektId: String(item.projektId),
          von: item.periode.von,
          bis: item.periode.bis,
        });
        if (item.kundeNr) params.set('kundeNr', item.kundeNr);
        return (
          <Link
            key={`${item.projektId}-${item.periode.von}-${i}`}
            to={`/rechnungen/neu?${params.toString()}`}
            className="flex items-center justify-between rounded-md border border-hair px-3 py-2 text-sm transition-colors hover:bg-bg-alt"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate font-medium">{item.projektName}</div>
              <div className="text-xs text-muted-foreground">
                {item.kundeName} · {item.periode.label}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              {item.schaetzungNetto > 0 && (
                <span
                  className="font-mono text-xs text-muted-foreground"
                  title="Geschaetzter Netto-Umsatz (ohne MwSt) basierend auf Tagessatz × 20 Tage"
                >
                  {formatCurrency(item.schaetzungNetto)}{' '}
                  <span className="text-[9px] font-medium uppercase tracking-wider">netto</span>
                </span>
              )}
              {item.periode.status === 'overdue' ? (
                <Badge variant="destructive">Überfällig</Badge>
              ) : (
                <Badge variant="warning">Fällig</Badge>
              )}
            </div>
          </Link>
        );
      })}
      {rest > 0 && (
        <p className="pt-1 text-xs text-muted-foreground">
          … und {rest} weitere in der{' '}
          <Link to="/abrechnungszeitraeume" className="text-primary hover:underline">
            Matrix
          </Link>
          .
        </p>
      )}
    </div>
  );
}

// =============================================================================
// Hero-Card — 1/3 hell (Greeting) + 2/3 dunkel (Offene Forderungen)
// mit Ring-Connector als Signature-Motiv
// =============================================================================

interface HeroCardProps {
  userName: string;
  offeneCount: number;
  offeneSumme: number;
  ueberfaelligCount: number;
  umsatzYtd: number;
  /** Jahr, auf das sich `umsatzYtd` bezieht — folgt der Monatsnavigation.
   *  „YTD" gilt nur fuers laufende Jahr; fuer ein abgeschlossenes Jahr waere
   *  das Label sachlich falsch, deshalb wird es dort zur Jahreszahl. */
  umsatzJahr: number;
  kundenAktiv: number;
  projekteAktiv: number;
  /** Optional: Klick-Handler fuer "Bericht exportieren". Wenn gesetzt,
   *  rendert ein Download-Button neben "Neue Rechnung" / "Mahnwesen". */
  onExportReport?: () => void;
}

function HeroCard({
  userName,
  offeneCount,
  offeneSumme,
  ueberfaelligCount,
  umsatzYtd,
  umsatzJahr,
  kundenAktiv,
  projekteAktiv,
  onExportReport,
}: HeroCardProps) {
  const now = new Date();
  const hour = now.getHours();
  const greeting = hour < 11 ? 'Guten Morgen' : hour < 18 ? 'Guten Tag' : 'Guten Abend';
  const kw = getWeekNumber(now);
  // „YTD" nur solange der Bezug wirklich das laufende Jahr ist.
  const umsatzLabel = umsatzJahr === now.getFullYear() ? 'Umsatz YTD' : `Umsatz ${umsatzJahr}`;

  return (
    <div className="fakt-panel relative grid min-h-[240px] overflow-hidden md:grid-cols-[1fr_1.6fr]">
      {/* Links: heller Bereich */}
      <div className="flex flex-col justify-center px-7 py-7">
        {/* Fix 17.08.2026: war auf `now.getFullYear()` festverdrahtet, obwohl
            `umsatzJahr`/`umsatzLabel` direkt darunter der Monatsnavigation
            folgen (Fix 12.08.2026) — ein Klick zurueck in den Dezember 2025
            zeigte "Geschäftsjahr 2026" ueber "Umsatz 2025 …" auf derselben
            Karte. KW bleibt an `now` haengen (Kalenderwoche ist immer die
            heutige, unabhaengig von der Umsatz-Navigation). */}
        <div className="mb-3 text-[10px] font-medium uppercase tracking-widest text-ink-muted">
          Geschäftsjahr {umsatzJahr} · KW {kw}
        </div>
        <SectionTitle size={26} underline={0.32}>
          {greeting}, {userName}
        </SectionTitle>
        <p className="mt-3 max-w-[320px] text-[13px] text-ink-soft">
          <strong className="text-avameo-primary">{ueberfaelligCount} Rechnungen</strong> sind
          überfällig,{' '}
          <strong className="text-ink">{offeneCount - ueberfaelligCount} weitere</strong> offen.{' '}
          {umsatzLabel} <strong className="text-ink">{formatCurrency(umsatzYtd)}</strong>{' '}
          <span className="text-[11px] uppercase tracking-wider text-ink-muted">netto</span>.
        </p>
        <div className="mt-5 flex flex-wrap gap-2">
          <Button asChild>
            <Link to="/rechnungen/neu">Neue Rechnung</Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/mahnwesen">Mahnwesen</Link>
          </Button>
          {onExportReport && (
            <Button
              variant="outline"
              size="sm"
              onClick={onExportReport}
              title="Aktuelles Dashboard als PDF-Bericht herunterladen — fuer Steuerberater, Geschaeftsleitung oder Archiv"
            >
              <Download className="h-4 w-4" />
              Bericht
            </Button>
          )}
        </div>
      </div>

      {/* Rechts: heller Bereich (05.05.2026) — vorher dunkler Hintergrund mit
           weisser Schrift; User-Wunsch: einheitlich helles Theme. Subtile
           Trennlinie zur linken Kachel via border-l. Ring-Connectors und
           Ring-Motiv entfernt (auch User-Wunsch). */}
      <div className="relative px-6 py-7 md:border-l md:border-hair md:px-8">
        <div className="flex items-baseline gap-2">
          <div className="text-[11px] uppercase tracking-wider text-ink-muted">
            Offene Forderungen
          </div>
          <span
            className="inline-flex items-center text-[10px] font-medium uppercase tracking-wider text-ink-muted"
            title="Brutto — inkl. Mehrwertsteuer (was der Kunde zahlt)"
          >
            · Brutto
          </span>
        </div>
        <div className="mt-2 flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <div className="text-3xl font-light leading-none tracking-tight text-ink sm:text-4xl lg:text-[42px]">
            {formatCurrency(offeneSumme)}
          </div>
          <div className="text-[13px] text-ink-soft">von {offeneCount} Rechnungen</div>
        </div>

        {/* Alterungs-Balken (statisch-berechnet) */}
        {offeneCount > 0 && (
          <div className="mt-5">
            <div className="flex h-[6px] overflow-hidden rounded bg-bg-alt">
              {(() => {
                const faellig = Math.max(0, offeneCount - ueberfaelligCount);
                const pctOk = Math.round((faellig / offeneCount) * 100);
                const pctOverdue = 100 - pctOk;
                return (
                  <>
                    <div style={{ width: `${pctOk}%`, background: 'var(--c-signal-ok)' }} />
                    <div style={{ width: `${pctOverdue}%`, background: 'var(--c-signal-error)' }} />
                  </>
                );
              })()}
            </div>
            <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] text-ink-muted">
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block h-2 w-2 rounded-sm"
                  style={{ background: 'var(--c-signal-ok)' }}
                />{' '}
                nicht überfällig · {offeneCount - ueberfaelligCount}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <span
                  className="inline-block h-2 w-2 rounded-sm"
                  style={{ background: 'var(--c-signal-error)' }}
                />{' '}
                überfällig · {ueberfaelligCount}
              </span>
            </div>
          </div>
        )}

        {/* 3 Mini-KPIs */}
        <div className="mt-6 grid grid-cols-3 gap-5">
          <HeroStat
            value={`${kundenAktiv}`}
            label="Aktive Kunden"
            dotColor="var(--c-brand-primary)"
          />
          <HeroStat
            value={`${projekteAktiv}`}
            label="Aktive Projekte"
            dotColor="var(--c-signal-ok)"
          />
          <HeroStat
            value={formatCurrency(umsatzYtd)}
            label={umsatzLabel}
            kind="netto"
            dotColor="var(--c-brand-secondary)"
          />
        </div>
      </div>
    </div>
  );
}

function HeroStat({
  value,
  label,
  dotColor,
  kind,
}: {
  value: string;
  label: string;
  dotColor: string;
  kind?: 'brutto' | 'netto';
}) {
  return (
    <div>
      <div className="flex items-baseline gap-1.5">
        <div className="text-[22px] font-light tabular-nums text-ink">{value}</div>
        {kind && (
          <span
            className="text-[9px] font-medium uppercase tracking-wider text-ink-muted"
            title={
              kind === 'brutto'
                ? 'Brutto — inkl. Mehrwertsteuer'
                : 'Netto — ohne Mehrwertsteuer (Umsatz/BWA)'
            }
          >
            {kind}
          </span>
        )}
      </div>
      <div className="mt-1 inline-flex items-center gap-1.5 text-[11px] text-ink-muted">
        <span className="inline-block h-2 w-2 rounded-sm" style={{ background: dotColor }} />
        {label}
      </div>
    </div>
  );
}

function getWeekNumber(d: Date): number {
  const date = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const dayNum = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - dayNum);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  return Math.ceil(((date.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
}

// ─── BL-064: DSO-KPI-Card ──────────────────────────────────────────────────
//
// Zeigt die wichtigsten Zahlungsdauer-Kennzahlen kompakt als KPI-Card.
// Pünktlichkeitsquote wird farblich codiert: >90% grün, >70% amber, rot.

function DsoKpiCard({ dso }: { dso: DsoData | null }) {
  if (!dso || dso.count === 0) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Zahlungsdauer (DSO)
          </CardTitle>
          <Clock className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-muted-foreground">—</div>
          <CardDescription className="mt-1">Noch keine bezahlten Rechnungen</CardDescription>
        </CardContent>
      </Card>
    );
  }

  const pktlich = dso.pktlichQuote !== null ? Math.round(dso.pktlichQuote * 100) : null;
  const pktlichColor =
    pktlich === null
      ? 'text-muted-foreground'
      : pktlich >= 90
        ? 'text-emerald-600 dark:text-emerald-400'
        : pktlich >= 70
          ? 'text-amber-600 dark:text-amber-400'
          : 'text-destructive';

  const verzugLabel =
    dso.avgVerzug === null
      ? null
      : dso.avgVerzug <= 0
        ? `${Math.abs(dso.avgVerzug)} Tage früher`
        : `${dso.avgVerzug} Tage zu spät`;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium text-muted-foreground">
          Zahlungsdauer (DSO)
        </CardTitle>
        <Clock className="h-4 w-4 text-primary" />
      </CardHeader>
      <CardContent>
        <div className="flex items-baseline gap-2">
          <div className="text-2xl font-bold">
            {dso.avgZahlungsdauer !== null ? `${dso.avgZahlungsdauer} Tage` : '—'}
          </div>
        </div>
        <div className="mt-1 space-y-0.5 text-[11px] text-ink-muted">
          {verzugLabel && <div>Ø Verzug: {verzugLabel}</div>}
          {pktlich !== null && (
            <div className={cn('font-medium', pktlichColor)}>{pktlich}% pünktlich bezahlt</div>
          )}
          <div className="text-ink-muted/70">{dso.count} Rechnungen (12 Monate)</div>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── Review 10.08.2026: Personalcontrolling-KPI-Card ──────────────────────
//
// Zeigt die Ø Auslastung des aktuellen Monats — nur über Consultants mit
// bereits erfasster Zelle, damit ein noch leerer Monatsanfang nicht wie 0%
// Auslastung aussieht. Warnungen (Datenqualität) werden als Badge
// hervorgehoben, weil sie sonst in der Matrix leicht übersehen werden.

function PersonalcontrollingKpiCard({
  summary,
  canWrite,
}: {
  summary: PersonalcontrollingSummary | null;
  canWrite: boolean;
}) {
  // Fix 19.08.2026: eigener Zustand fuer VIEWER statt der jetzt 403-gated
  // Query einfach wie "keine aktiven Consultants" aussehen zu lassen (vgl.
  // AusgabenKpiCard/LohnKpiCard). Kein Link auf /personalcontrolling — die
  // Seite dort wuerde dieselbe Sperre nur wiederholen.
  if (!canWrite) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Ø Auslastung diesen Monat
          </CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-muted-foreground">—</div>
          <CardDescription className="mt-1">Admin/Buchhaltung vorbehalten</CardDescription>
        </CardContent>
      </Card>
    );
  }

  if (!summary || summary.consultantsAktiv === 0) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Ø Auslastung diesen Monat
          </CardTitle>
          <Users className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-muted-foreground">—</div>
          <CardDescription className="mt-1">Keine aktiven Consultants</CardDescription>
        </CardContent>
      </Card>
    );
  }

  // BL-195 (Fund Finance-/PM-Sparringspartner): eine nackte Prozentzahl aus
  // z.B. nur 1 von 9 erfassten Zellen wirkt vollständig, ist es aber nicht —
  // dieselbe Klasse-B-Fehlerklasse wie BL-181/BL-194 (Zahl täuscht
  // Vollständigkeit vor), nur an anderer Stelle. Unter 50% Erfassung zeigt
  // die Kachel deshalb einen Klartext-Hinweis statt der Zahl, analog zum
  // `umsatzseiteImportiert`-Muster in der FiBu-Ergebnis-Rechnung. Die
  // "N von M erfasst"-Zeile steht ohnehin schon direkt darunter — der Wert
  // wird hier bewusst nicht ein zweites Mal in den Kachel-Titel eingebettet.
  const erfassungsQuote = summary.cellsErfasst / summary.consultantsAktiv;
  const datenlageUnzureichend = erfassungsQuote < 0.5;

  return (
    <Link to="/personalcontrolling" className="block">
      <Card className="transition-colors hover:bg-bg-alt/40">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Ø Auslastung diesen Monat
          </CardTitle>
          <Users className="h-4 w-4 text-primary" />
        </CardHeader>
        <CardContent>
          <div
            className={
              datenlageUnzureichend
                ? 'text-base font-semibold text-amber-600 dark:text-amber-400'
                : 'text-2xl font-bold'
            }
          >
            {datenlageUnzureichend
              ? 'Datenlage unzureichend'
              : summary.avgAuslastung !== null
                ? `${Math.round(summary.avgAuslastung * 100)}%`
                : '—'}
          </div>
          <div className="mt-1 space-y-0.5 text-[11px] text-ink-muted">
            <div>
              {summary.cellsErfasst} von {summary.consultantsAktiv} Consultants erfasst
            </div>
            {summary.warnungenCount > 0 && (
              <div className="flex items-center gap-1 font-medium text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3 w-3" />
                {summary.warnungenCount} Datenqualitäts-Hinweis
                {summary.warnungenCount === 1 ? '' : 'e'}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// ─── Ausgaben-Kachel (Ausgaben-Reporting) ──────────────────────────────────
//
// Zeigt den Gesamt-Aufwand des neuesten importierten SuSa-Monats — NICHT
// zwingend der aktuelle Kalendermonat, weil die Steuerkanzlei ihre PDFs
// typischerweise erst Wochen nach Monatsende liefert. `summary === null`
// bedeutet "noch nie importiert", nicht "0 € Ausgaben".

function AusgabenKpiCard({
  summary,
  canWrite,
  isLoading,
  isError,
}: {
  summary: AusgabenSummary | null;
  canWrite: boolean;
  isLoading: boolean;
  isError: boolean;
}) {
  // BL-347 (16.09.2026, externes Anwendungsreview): "Noch kein Monat
  // importiert" wurde auch fuer laufende/fehlgeschlagene Abfragen gezeigt —
  // bei sechs tatsaechlich vorhandenen Jahres-Master-Monaten auf Prod fachlich
  // falsch. `pickKanzleiTileState` unterscheidet die vier Faelle explizit statt
  // sie ueber verschachtelte `if (!x)`-Checks implizit zu vermischen; die reale
  // Backend-Logik ist unveraendert korrekt (siehe `getLatestActiveBatchInfo`,
  // durch `susa-jahres-master.test.ts` gedeckt).
  const state = pickKanzleiTileState({ canWrite, isLoading, isError, hasSummary: !!summary });

  if (state === 'locked') {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">FiBu</CardTitle>
          <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-muted-foreground">—</div>
          <CardDescription className="mt-1">Admin/Buchhaltung vorbehalten</CardDescription>
        </CardContent>
      </Card>
    );
  }

  if (state === 'loading') {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">FiBu</CardTitle>
          <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-muted-foreground">—</div>
          <CardDescription className="mt-1">Wird geladen …</CardDescription>
        </CardContent>
      </Card>
    );
  }

  if (state === 'error') {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">FiBu</CardTitle>
          <FileSpreadsheet className="h-4 w-4 text-destructive" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-muted-foreground">—</div>
          <CardDescription className="mt-1 text-destructive">
            Fehler beim Laden — nicht mit "noch nie importiert" verwechseln
          </CardDescription>
        </CardContent>
      </Card>
    );
  }

  if (state === 'empty' || !summary) {
    return (
      <Link to="/ausgaben" className="block">
        <Card className="transition-colors hover:bg-bg-alt/40">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">FiBu</CardTitle>
            <FileSpreadsheet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-muted-foreground">—</div>
            <CardDescription className="mt-1">Noch kein Monat importiert</CardDescription>
          </CardContent>
        </Card>
      </Link>
    );
  }

  // Beim Jahres-Master ist `gesamtAufwand` der JAHRESwert — ihn mit dem
  // Monatsnamen zu beschriften waere eine glatte Falschaussage (Faktor ~12).
  const periodLabel =
    summary.periodenTyp === 'JAHR'
      ? String(summary.jahr)
      : new Date(summary.jahr, summary.monat - 1, 1).toLocaleDateString(LOCALE_DE, {
          month: 'long',
          year: 'numeric',
        });

  return (
    <Link to="/ausgaben" className="block">
      <Card className="transition-colors hover:bg-bg-alt/40">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            Gesamtkosten FiBu {periodLabel}
          </CardTitle>
          <FileSpreadsheet className="h-4 w-4 text-primary" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{formatCurrency(summary.gesamtAufwand)}</div>
          <div className="mt-1 text-[11px] text-ink-muted">
            Kanzlei-Daten · {summary.periodenTyp === 'JAHR' ? 'Jahresstand' : 'Stand'}{' '}
            {formatDate(summary.standDatum)}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// ─── Lohn-Kachel (LoBu-Reporting) ───────────────────────────────────────────
//
// Analog AusgabenKpiCard: zeigt nur den AGGREGIERTEN Gesamtwert des
// neuesten importierten Lohnjournal-Monats — nie Einzelpersonen-Daten
// (Reporter-Konsultation, siehe Plan). `summary === null` bedeutet "noch nie
// importiert", nicht "0 € Personalkosten".

function LohnKpiCard({
  summary,
  canWrite,
  isLoading,
  isError,
}: {
  summary: LohnSummary | null;
  canWrite: boolean;
  isLoading: boolean;
  isError: boolean;
}) {
  // BL-347 (16.09.2026): siehe AusgabenKpiCard -- dieselbe Luecke (Loading/
  // Error sahen aus wie "noch nie importiert"), hier analog geschlossen.
  const state = pickKanzleiTileState({ canWrite, isLoading, isError, hasSummary: !!summary });

  if (state === 'locked') {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">LoBu</CardTitle>
          <Users2 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-muted-foreground">—</div>
          <CardDescription className="mt-1">Admin/Buchhaltung vorbehalten</CardDescription>
        </CardContent>
      </Card>
    );
  }

  if (state === 'loading') {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">LoBu</CardTitle>
          <Users2 className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-muted-foreground">—</div>
          <CardDescription className="mt-1">Wird geladen …</CardDescription>
        </CardContent>
      </Card>
    );
  }

  if (state === 'error') {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">LoBu</CardTitle>
          <Users2 className="h-4 w-4 text-destructive" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold text-muted-foreground">—</div>
          <CardDescription className="mt-1 text-destructive">
            Fehler beim Laden — nicht mit "noch nie importiert" verwechseln
          </CardDescription>
        </CardContent>
      </Card>
    );
  }

  if (state === 'empty' || !summary) {
    return (
      <Link to="/lohnbuchhaltung" className="block">
        <Card className="transition-colors hover:bg-bg-alt/40">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">LoBu</CardTitle>
            <Users2 className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-muted-foreground">—</div>
            <CardDescription className="mt-1">Noch kein Monat importiert</CardDescription>
          </CardContent>
        </Card>
      </Link>
    );
  }

  const monthLabel = new Date(summary.jahr, summary.monat - 1, 1).toLocaleDateString(LOCALE_DE, {
    month: 'long',
    year: 'numeric',
  });

  return (
    <Link to="/lohnbuchhaltung" className="block">
      <Card className="transition-colors hover:bg-bg-alt/40">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium text-muted-foreground">
            LoBu {monthLabel}
          </CardTitle>
          <Users2 className="h-4 w-4 text-primary" />
        </CardHeader>
        <CardContent>
          <div className="text-2xl font-bold">{formatCurrency(summary.gesamtBrutto)}</div>
          <div className="mt-1 text-[11px] text-ink-muted">
            Kanzlei-Daten · {summary.mitarbeiterAnzahl} Mitarbeiter · Stand{' '}
            {formatDate(summary.standDatum)}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

// ─── BL-064: Zahlungsverhalten pro Kunde ───────────────────────────────────
//
// Tabelle der Top-10 Kunden nach Anzahl Rechnungen mit Ø Zahlungsdauer
// und Verzug. Ampel-Farbe für Verzug: ≤0 grün, ≤14 amber, >14 rot.

// BL-235(g): bei 0 gab `<= 0` fälschlich ein Minuszeichen vor der "0" aus.
function formatVerzug(n: number): string {
  return n === 0 ? '0' : n < 0 ? `−${Math.abs(n)}` : `+${n}`;
}
function DsoByKundeCard({ dso }: { dso: DsoData }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="h-4 w-4 text-primary" />
          Zahlungsverhalten pro Kunde
        </CardTitle>
        <CardDescription>
          Letzte 12 Monate · {dso.count} bezahlte Rechnungen · Ø {dso.avgZahlungsdauer} Tage
          Zahlungsdauer
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-hair text-left text-[11px] font-medium uppercase tracking-wider text-ink-muted">
                <th className="pb-2 pr-4">Kunde</th>
                <th className="pb-2 pr-4 text-right">Rechnungen</th>
                <th className="pb-2 pr-4 text-right">Ø Zahlungsdauer</th>
                <th className="pb-2 text-right">Ø Verzug</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-hair">
              {dso.byKunde.map((k) => {
                const verzugColor =
                  k.avgVerzug <= 0
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : k.avgVerzug <= 14
                      ? 'text-amber-600 dark:text-amber-400'
                      : 'text-destructive';
                return (
                  <tr key={k.kundeNr ?? k.kunde} className="hover:bg-bg-alt/40">
                    <td className="py-2 pr-4">
                      {k.kundeNr ? (
                        <Link
                          to={`/kunden/${k.kundeNr}`}
                          className="font-medium text-primary hover:underline"
                        >
                          {k.kunde}
                        </Link>
                      ) : (
                        <span className="font-medium">{k.kunde}</span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-right tabular-nums text-ink-muted">{k.count}</td>
                    <td className="py-2 pr-4 text-right tabular-nums">{k.avgDays} T</td>
                    <td className={cn('py-2 text-right tabular-nums font-medium', verzugColor)}>
                      {formatVerzug(k.avgVerzug)} T
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
}

// ─── BL-058: Setup-Checklist-Banner ────────────────────────────────────────
//
// Solange Pflicht-Settings + Mindest-Stammdaten fehlen, zeigt der Banner
// oben am Dashboard die Liste der offenen Punkte mit direkten Fix-Links.
// Sobald complete=true: gar nichts mehr rendern (kein Erfolgs-Banner —
// auf Dashboards stört positive Bestätigung mehr als sie nutzt).

interface SetupChecklistItem {
  key: string;
  label: string;
  ok: boolean;
  hint?: string;
  fixHref?: string;
  group: 'firmendaten' | 'bank' | 'stammdaten';
}

interface SetupStatusResponse {
  items: SetupChecklistItem[];
  complete: boolean;
  openCount: number;
}

function SetupChecklistBanner() {
  const query = useQuery({
    queryKey: ['setup-status'],
    queryFn: async () => (await api.get<SetupStatusResponse>('/setup-status')).data,
    // 5 min cache reicht — Settings ändern sich selten und der Banner
    // verschwindet eh nach manuellem Edit (dann invalidiert TanStack-Query
    // beim Settings-PUT via audit-middleware).
    staleTime: 5 * 60 * 1000,
  });

  if (!query.data || query.data.complete) return null;

  const offene = query.data.items.filter((i) => !i.ok);
  return (
    <Card className="border-amber-300 bg-amber-50/40 dark:border-amber-700/50 dark:bg-amber-900/15">
      <CardHeader className="flex flex-row items-center gap-3 space-y-0 pb-2">
        <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600 dark:text-amber-400" />
        <div>
          <CardTitle className="text-base text-amber-900 dark:text-amber-100">
            {query.data.openCount} Pflicht-Konfiguration{query.data.openCount === 1 ? '' : 'en'}{' '}
            fehl{query.data.openCount === 1 ? 't' : 'en'}
          </CardTitle>
          <CardDescription>
            Bevor die erste Rechnung geschrieben werden kann, sollte diese Checkliste komplett sein.
            USt-IdNr oder IBAN auf einer Rechnung zu vergessen ist eine Findung bei der nächsten
            Betriebsprüfung.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent>
        <ul className="space-y-1.5 text-sm">
          {offene.map((item) => (
            <li key={item.key} className="flex items-start gap-2">
              <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-700 dark:text-amber-300" />
              <span className="flex-1">{item.label}</span>
              {item.fixHref && (
                <Link
                  to={item.fixHref}
                  className="shrink-0 text-xs font-medium text-primary hover:underline"
                >
                  einrichten →
                </Link>
              )}
            </li>
          ))}
        </ul>
        {/* OK-Items werden ausgeblendet (würde nur stören) — Counter-Hint genügt. */}
        <p className="mt-3 text-[11px] text-ink-muted">
          {query.data.items.length - query.data.openCount}/{query.data.items.length} bereits
          eingerichtet
        </p>
      </CardContent>
    </Card>
  );
}
